import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  BookOpen, LogIn, MapPin, ShoppingCart, Wallet, Truck, Pill,
  Receipt, Calculator, KeyRound, HelpCircle, ChevronDown, CheckCircle2,
} from "lucide-react";

const ROLE_LABELS = { patron: "Patron", vendeur: "Vendeur", associe: "Associé" };

function Step({ children }) {
  return (
    <li className="flex gap-2.5">
      <CheckCircle2 className="w-4 h-4 text-brand mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function Section({ id, icon: Icon, title, tag, isOpen, onToggle, children }) {
  return (
    <div className="bg-white border border-brand-line rounded-2xl overflow-hidden" data-testid={`guide-section-${id}`}>
      <button
        onClick={onToggle}
        data-testid={`guide-toggle-${id}`}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-brand-cream/50 transition-colors"
      >
        <span className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-brand" />
        </span>
        <span className="flex-1">
          <span className="font-heading font-bold text-brand-ink block">{title}</span>
          {tag && <span className="text-xs text-brand-muted">{tag}</span>}
        </span>
        <ChevronDown className={`w-5 h-5 text-brand-muted transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-1 text-sm text-brand-sub leading-relaxed border-t border-brand-line/60">
          {children}
        </div>
      )}
    </div>
  );
}

export default function Guide() {
  const { user } = useAuth();
  const role = user?.role || "vendeur";
  const [open, setOpen] = useState("login");
  const toggle = (id) => setOpen((cur) => (cur === id ? null : id));

  const sections = [
    {
      id: "login", icon: LogIn, title: "1. Se connecter", roles: ["patron", "vendeur", "associe"],
      tag: "Tout le monde",
      body: (
        <ul className="space-y-2 mt-3">
          <Step>Ouvrez le site puis allez sur la page <b>Connexion</b> (bouton en haut, ou l'adresse <b>/login</b>).</Step>
          <Step>Entrez votre <b>e-mail</b> et votre <b>mot de passe</b> fournis par le Patron.</Step>
          <Step>Cliquez sur <b>Se connecter</b>. Vous arrivez sur le <b>Tableau de bord</b>.</Step>
          <Step>À la 1ʳᵉ connexion, changez votre mot de passe : menu de gauche → <b>Changer mon mot de passe</b> 🔑.</Step>
          <Step><b>Astuce :</b> gardez le site ouvert pendant la journée pour entendre le <b>bip 🔔 des nouvelles commandes</b>.</Step>
        </ul>
      ),
    },
    {
      id: "pointage", icon: MapPin, title: "2. Pointage (présence)", roles: ["patron", "vendeur", "associe"],
      tag: "Vendeur surtout",
      body: (
        <ul className="space-y-2 mt-3">
          <Step>Sur le <b>Tableau de bord</b>, trouvez la carte <b>Pointage</b>.</Step>
          <Step>En <b>arrivant au travail</b> (Frigo Intercontinental), cliquez sur <b>Pointer l'arrivée</b>. Autorisez la <b>localisation</b> sur le téléphone.</Step>
          <Step>Le pointage est validé seulement si vous êtes <b>sur place</b> (rayon de 500 m). Sinon un message rouge s'affiche.</Step>
          <Step>En <b>fin de journée</b>, cliquez sur <b>Pointer le départ</b>.</Step>
          <Step><b>Important :</b> au départ, un <b>résumé du jour est envoyé automatiquement par e-mail</b> au Patron. Pointez donc votre départ chaque soir.</Step>
        </ul>
      ),
    },
    {
      id: "commandes", icon: ShoppingCart, title: "3. Ventes & commandes", roles: ["patron", "vendeur", "associe"],
      tag: "Tout le monde",
      body: (
        <ul className="space-y-2 mt-3">
          <Step>Menu <b>Ventes & commandes</b> : vous voyez toutes les commandes (site, Live TikTok, ventes en boutique).</Step>
          <Step>Pour une <b>vente au comptant rapide</b> (client devant vous), utilisez la vente <b>Cash en 1 clic</b> : choisissez le produit, la quantité, validez.</Step>
          <Step>Chaque commande a un <b>statut de paiement</b> : Payé, En attente, ou À la livraison.</Step>
          <Step>Cliquez sur une commande pour voir le détail : client, produits, montant, mode de paiement.</Step>
          <Step>Quand une commande doit être livrée, elle apparaît aussi dans <b>Livraisons</b>.</Step>
        </ul>
      ),
    },
    {
      id: "caisse", icon: Wallet, title: "4. Caisse (argent & preuves de dépôt)", roles: ["patron", "vendeur", "associe"],
      tag: "Vendeur",
      body: (
        <ul className="space-y-2 mt-3">
          <Step>La carte <b>Caisse</b> (Tableau de bord) montre le <b>cash que vous avez encaissé</b> et le <b>montant à remettre</b> au Patron.</Step>
          <Step>Quand vous <b>déposez l'argent</b> (banque, Mobile Money ou en main propre), cliquez sur <b>Enregistrer un dépôt</b>.</Step>
          <Step>Indiquez le <b>montant</b> et la <b>méthode</b> (espèces, Flooz, T-Money…).</Step>
          <Step><b>Prenez une photo de la preuve</b> (reçu, SMS de transfert) et ajoutez-la. C'est obligatoire pour valider.</Step>
          <Step>Le Patron voit le dépôt dans <b>Comptabilité</b> et peut le <b>valider</b>. Votre « cash à remettre » redescend à 0.</Step>
        </ul>
      ),
    },
    {
      id: "livraisons", icon: Truck, title: "5. Livraisons", roles: ["patron", "vendeur", "associe"],
      tag: "Tout le monde",
      body: (
        <ul className="space-y-2 mt-3">
          <Step>Menu <b>Livraisons</b> : liste des commandes à livrer au Togo.</Step>
          <Step>Quand le livreur part, cliquez sur <b>Livreur en route</b> → un message <b>WhatsApp</b> prêt à envoyer au client s'ouvre.</Step>
          <Step>Une fois remis au client, cliquez sur <b>Commande livrée</b> → un 2ᵉ message WhatsApp de confirmation s'ouvre.</Step>
          <Step>Le <b>montant à payer au livreur</b> (négocié) est suivi dans le menu <b>Livreurs (règlement)</b>. C'est le <b>vendeur sur place</b> qui paie le livreur (cochez « J'ai déjà payé le livreur »).</Step>
          <Step><b>Fonds livreur :</b> le Patron/Associé dépose une <b>avance</b> (ex : 20 000) au vendeur, dédiée aux livreurs. Chaque paiement diminue le solde ; une alerte apparaît sous 5 000 FCFA pour recharger.</Step>
          <Step>Envoyez toujours le message WhatsApp : le client est rassuré et suit sa commande.</Step>
        </ul>
      ),
    },
    {
      id: "studio", icon: BookOpen, title: "6b. Studio Pub (TikTok)", roles: ["patron", "vendeur", "associe"],
      tag: "Marketing",
      body: (
        <ul className="space-y-2 mt-3">
          <Step>Menu <b>Studio Pub</b> : créez vos contenus pub pour TikTok. Choisissez un produit en haut (ou « général »).</Step>
          <Step><b>Scripts TikTok :</b> choisissez un ton, cliquez « Générer le script » → l'IA écrit un <b>script vidéo</b>, une <b>légende</b> et des <b>hashtags</b>. Cliquez « Copier ».</Step>
          <Step><b>Affiches (IA) :</b> décrivez l'affiche voulue (ex : « promo -10% ») → l'IA crée une <b>affiche</b> prête à publier. Copiez le lien ou téléchargez.</Step>
          <Step><b>Médiathèque :</b> uploadez vos propres <b>vidéos</b> (max 45 Mo) filmées au téléphone, puis <b>copiez le lien</b> à partager sur TikTok/WhatsApp.</Step>
        </ul>
      ),
    },
    {
      id: "pharmacies", icon: Pill, title: "6. Dépôts pharmacie (consignations)", roles: ["patron", "vendeur", "associe"],
      tag: "Vendeur / Associé",
      body: (
        <ul className="space-y-2 mt-3">
          <Step>Menu <b>Dépôts pharmacie</b> : suivi des produits laissés en dépôt dans les pharmacies.</Step>
          <Step>Pour un nouveau dépôt : <b>nom de la pharmacie</b>, <b>produits déposés</b> et <b>valeur totale</b>.</Step>
          <Step>Quand la pharmacie vous <b>paie</b> une partie, entrez le <b>montant encaissé</b>.</Step>
          <Step>La <b>valeur restante</b> (encore à récupérer) se met à jour toute seule.</Step>
          <Step>Le total « à encaisser (pharmacies) » apparaît aussi dans la <b>Comptabilité</b>.</Step>
        </ul>
      ),
    },
    {
      id: "depenses", icon: Receipt, title: "7. Dépenses", roles: ["patron", "associe"],
      tag: "Patron / Associé",
      body: (
        <ul className="space-y-2 mt-3">
          <Step>Menu <b>Dépenses</b> : enregistrez tout l'argent qui sort (achats, transport, salaires, loyer…).</Step>
          <Step>Pour chaque dépense : <b>montant</b>, <b>catégorie</b>, <b>description</b>, <b>fournisseur</b> et <b>mode de paiement</b>.</Step>
          <Step>Vous pouvez <b>exporter en CSV</b> pour Excel / votre comptable.</Step>
          <Step>Ces dépenses sont automatiquement déduites dans la <b>Comptabilité</b> (bénéfice net).</Step>
        </ul>
      ),
    },
    {
      id: "comptabilite", icon: Calculator, title: "8. Comptabilité", roles: ["patron", "associe"],
      tag: "Patron / Associé",
      body: (
        <ul className="space-y-2 mt-3">
          <Step>Menu <b>Comptabilité</b> : vue d'ensemble des finances par mois/année.</Step>
          <Step>Vous voyez le <b>Chiffre d'affaires</b>, les <b>Dépenses</b> et le <b>Bénéfice net</b> de la période.</Step>
          <Step>Vous voyez aussi les <b>sommes à récupérer / à payer</b> : cash des vendeurs, pharmacies, commissions, livreurs.</Step>
          <Step>Changez le <b>mois</b> et l'<b>année</b> en haut à droite pour comparer.</Step>
        </ul>
      ),
    },
    {
      id: "password", icon: KeyRound, title: "9. Sécurité & mot de passe", roles: ["patron", "vendeur", "associe"],
      tag: "Tout le monde",
      body: (
        <ul className="space-y-2 mt-3">
          <Step>Changez votre mot de passe régulièrement : menu de gauche → <b>Changer mon mot de passe</b>.</Step>
          <Step>Ne partagez <b>jamais</b> votre mot de passe. Chaque personne a son propre compte.</Step>
          <Step>Déconnectez-vous sur un appareil partagé : menu → <b>Déconnexion</b>.</Step>
        </ul>
      ),
    },
    {
      id: "faq", icon: HelpCircle, title: "10. Questions fréquentes", roles: ["patron", "vendeur", "associe"],
      tag: "Tout le monde",
      body: (
        <ul className="space-y-3 mt-3">
          <li><b>Je n'entends pas le bip des commandes ?</b> Vérifiez que le son est <b>activé</b> (menu de gauche, icône haut-parleur) et gardez le site ouvert.</li>
          <li><b>Mon pointage est refusé ?</b> Vous devez être <b>sur place</b> et avoir <b>autorisé la localisation</b> du téléphone.</li>
          <li><b>Je ne vois pas certains menus ?</b> C'est normal : les menus dépendent de votre rôle ({ROLE_LABELS[role]}).</li>
          <li><b>Mes changements n'apparaissent pas sur le vrai site ?</b> Prévenez le Patron : il faut cliquer sur <b>« Déployer »</b> pour publier en ligne.</li>
        </ul>
      ),
    },
  ].filter((s) => s.roles.includes(role));

  return (
    <div className="space-y-6" data-testid="guide-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink flex items-center gap-2">
          <BookOpen className="w-7 h-7 text-brand" /> Mode d'emploi
        </h1>
        <p className="text-brand-muted mt-1">
          Guide simple pour utiliser la plateforme. Vous êtes connecté en tant que <b>{ROLE_LABELS[role]}</b>.
        </p>
      </div>

      <div className="bg-gradient-to-br from-brand/10 to-brand-accent/10 border border-brand-line rounded-2xl p-5 text-sm text-brand-sub">
        👋 Bienvenue chez <b>NOUMABOU</b> ! Cliquez sur chaque étape ci-dessous pour la dérouler.
        Les étapes suivent l'ordre d'une journée type : <b>connexion → pointage → ventes → caisse → livraisons</b>.
      </div>

      <div className="space-y-3">
        {sections.map((s) => (
          <Section key={s.id} id={s.id} icon={s.icon} title={s.title} tag={s.tag} isOpen={open === s.id} onToggle={() => toggle(s.id)}>
            {s.body}
          </Section>
        ))}
      </div>

      <div className="bg-brand-ink rounded-2xl p-5 text-sm text-white/80" data-testid="guide-help-footer">
        Besoin d'aide ? Contactez le <b className="text-white">Patron</b> ou l'équipe support. Bonne vente ! 🚀
      </div>
    </div>
  );
}
